import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { App, Button, Tooltip } from 'antd';

import { api } from '../../api/client';
import { API_COLLECTIONS_URL } from '../../utils';
import RecordBadge from '../common/RecordBadge';
import ToggleRow from '../common/ToggleRow';
import { confirmDestructive } from '../common/confirmDestructive';
import ItemTagModeModal from '../collections/ItemTagModeModal';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { collectionCardsPath } from '../../config/collectionShell';
import { normalizeId } from '../../utils/ids';
import { formatCount } from '../../utils/counts';
import { cardCollectionsPath } from '../../utils/listLinks';
import { canManage } from '../../utils/permissions';
import { getCollectionCards } from '../../utils/collections';
import { inheritedCardIdsOf, parentOf } from '../../utils/collectionFamily';
import { isRetiredCard } from '../../utils/cardScopes';
import { itemTagSettingFor, itemTagSettingPhrase, itemTagsOn, ownItemTagMode, withoutItemTagKeys } from '../../utils/itemTagSettings';

/**
 * A GLOBAL card opened inside a collection (KPR-1285, KPE-039, ruling q08):
 * what leads its home in the collection's shell.
 *
 * - **Scope**, for everyone who reads the collection: a global card, kept by
 *   keepr, used in N collections. An admin gets the door to edit it, which is
 *   the platform page `/cards/:id` (a global card is never edited from inside
 *   one collection); `?collectionId=` keeps this collection's tile tiers in
 *   hand there.
 * - **This collection's own settings for it**, for the collection's managers
 *   only (the manage rank, which an admin's `myAccess` carries, as the API
 *   reads it): Pin for everyone, the tile layout (its This collection tier),
 *   Use items as tags, and Remove from the collection, last and confirmed.
 *   Each writes at once, as on the Cards tab's row: the pin and the removal
 *   through the collection PUT (a `$set` of `cards` alone, against the
 *   version read, item-tag keys never sent), Use items as tags through its
 *   own endpoint.
 *
 * A card the collection holds only through the collection above it says so,
 * and offers no Remove: there is nothing of this collection's to remove.
 */
/** The admin's door to edit a global card: its platform page, from this collection. */
export const globalCardEditPath = (cardId, collectionId) => `/cards/${cardId}${collectionId ? `?collectionId=${collectionId}` : ''}`;

export default function GlobalCardInCollection({ card, collection, usage = null, admin = false, cardsById = null, onArrangeTile }) {
	const navigate = useNavigate();
	const { message, modal } = App.useApp();
	const layout = useCollectionContext();
	const [busy, setBusy] = useState(false);
	const [itemTagsOpen, setItemTagsOpen] = useState(false);

	const cardId = normalizeId(card?._id);
	const collectionId = normalizeId(collection?._id);
	if (!cardId || !collectionId) return null;

	const cardName = (card?.name || '').trim() || 'This card';
	const collectionName = (collection?.name || '').trim() || 'this collection';
	const manager = canManage(collection);
	const refs = getCollectionCards(collection);
	const ref = refs.find(entry => normalizeId(entry?.cardDefinitionId) === cardId) || null;
	const inherited = inheritedCardIdsOf(collection).has(cardId);
	const pinned = Boolean(ref?.favorite);
	const itemTags = itemTagSettingFor(collection, cardId, cardsById);
	const ownTagsOn = itemTagsOn(ownItemTagMode(ref));
	const usedIn = Array.isArray(usage?.collections) ? usage.collections.length : null;
	const from = inherited ? parentOf(collection) : null;

	// The collection's `cards[]` with this one change, sent alone against the
	// version this page read. The layout's copy moves with it, so the next
	// write is against the version this one stamped.
	const writeCards = async (next, done) => {
		setBusy(true);
		try {
			const body = { cards: withoutItemTagKeys(next) };
			if (collection.updatedAt) body.ifUpdatedAt = collection.updatedAt;
			const res = await api.put(`${API_COLLECTIONS_URL}/${collectionId}`, body);
			const updatedAt = res?.data?.updatedAt || null;
			done?.();
			layout?.setCollection?.(prev => (normalizeId(prev?._id) === collectionId ? { ...prev, cards: next, ...(updatedAt ? { updatedAt } : null) } : prev));
			if (!updatedAt) layout?.refreshCollection?.();
			return true;
		} catch (error) {
			const data = error?.response?.data;
			if (error?.response?.status === 409 && data?.code === 'stale_collection') {
				message.error(`${collectionName} changed since this page opened. Look again, then try once more.`);
				layout?.refreshCollection?.();
			} else {
				message.error(data?.message || 'That did not save. Try again.');
			}
			return false;
		} finally {
			setBusy(false);
		}
	};

	const togglePin = () => {
		const next = ref
			? refs.map(entry => (normalizeId(entry?.cardDefinitionId) === cardId ? { ...entry, favorite: !pinned } : entry))
			: [...refs, { cardDefinitionId: cardId, name: card?.name, favorite: true }];
		writeCards(next, () => message.success(pinned ? `Unpinned for everyone in ${collectionName}.` : `Pinned for everyone in ${collectionName}.`));
	};

	const confirmRemove = () => confirmDestructive(modal, {
		title: `Remove the "${cardName}" card from ${collectionName}?`,
		// A retired card (KPR-280) cannot be added back once it is removed.
		content: isRetiredCard(card)
			? 'No new items here can use it. Items already made from it keep their data. This card is retired, so it cannot be added back.'
			: 'No new items here can use it. Items already made from it keep their data.',
		okText: 'Remove',
		onOk: () => writeCards(
			refs.filter(entry => normalizeId(entry?.cardDefinitionId) !== cardId),
			() => {
				message.success(`Removed from ${collectionName}.`);
				navigate(collectionCardsPath(collectionId));
			}
		),
	});

	const tagsPhrase = itemTagSettingPhrase(itemTags, { cardId, cardName: cardsById?.[itemTags?.fromCardId]?.name });

	return (
		<>
			<section className='form-panel card-global' data-testid='global-card-scope'>
				<div className='micro-label form-panel__kicker'>Scope</div>
				<p className='card-global__scope'>
					{'Global card, kept by keepr'}
					{usedIn ? <>{', used in '}<Link to={cardCollectionsPath({ key: card?.key, _id: cardId })}>{formatCount(usedIn, 'collection')}</Link></> : null}
					{'.'}
				</p>
				{from ? <p className='form-panel__hint'>{`${collectionName} uses it through ${from.name}.`}</p> : null}
				{admin ? (
					<p className='card-global__edit'>
						<Link to={globalCardEditPath(cardId, collectionId)}>Edit the global card</Link>
						{' '}<span className='form-panel__hint'>Changes reach every collection that uses it.</span>
					</p>
				) : null}
			</section>

			{manager ? (
				<section className='form-panel card-global' data-testid='global-card-settings' aria-label={`${collectionName}'s settings for this card`}>
					<div className='micro-label form-panel__kicker'>This collection&rsquo;s settings</div>
					<div className='card-def-form__usage card-global__where'>
						<span>In</span>
						<RecordBadge kind='collection' record={collection} title={collectionName} size='xs' to={`/collections/${collectionId}`} />
					</div>
					<ToggleRow
						first
						label='Pin for everyone'
						description={`Puts it at the top of the card picker for everyone using ${collectionName}. It applies at once.`}
						checked={pinned}
						disabled={busy}
						onChange={togglePin}
					/>
					<ToggleRow label='Tile layout' description={`Which fields an item shows in ${collectionName}'s grid. It saves on its own.`}>
						<Button onClick={onArrangeTile}>Arrange tile&hellip;</Button>
					</ToggleRow>
					<ToggleRow label='Use items as tags' description={tagsPhrase || 'Off: its items are not tags here.'}>
						<Button onClick={() => setItemTagsOpen(true)}>Change&hellip;</Button>
					</ToggleRow>
					{ref && !inherited ? (
						<ToggleRow label={`Remove from ${collectionName}`} description='No new items here can use it. Items already made from it keep their data.'>
							<Tooltip title={ownTagsOn ? 'Its items are used as tags here. Turn Use items as tags off first.' : ''}>
								<Button danger disabled={busy || ownTagsOn} onClick={confirmRemove}>Remove&hellip;</Button>
							</Tooltip>
						</ToggleRow>
					) : null}
				</section>
			) : null}

			{itemTagsOpen ? (
				<ItemTagModeModal
					open
					onClose={() => setItemTagsOpen(false)}
					collectionId={collectionId}
					collectionName={collection?.name || null}
					card={{ id: cardId, name: card?.name, def: card }}
					cardsById={cardsById}
					archived={collection?.status === 'archived'}
					onChanged={() => layout?.refreshCollection?.()}
				/>
			) : null}
		</>
	);
}
